// Pair-strength calculator — ported from umag.html. Four players, six possible
// pairs, three possible partitions. Two modes: illustrative 1–5 scoring and the
// real Elo scoring. Switching modes loads that mode's preset (no value
// translation, which would be misleading).
import { useMemo, useState } from 'react';

import {
  type CalcMode,
  type CalcPlayer,
  type Cohort,
  type PlayerLabel,
  PLAYER_LABELS,
  PRESET_ELO,
  PRESET_SCORE,
  SCALE_MAX,
  ELO_MIN,
  ELO_MAX,
  ELO_STEP,
  allPairs,
  clonePreset,
  fmt,
  partitionsFromPairs,
} from './calc';

type SkillKey = keyof CalcPlayer;

const SKILLS: { key: SkillKey; label: string }[] = [
  { key: 'sz', label: 'szerva' },
  { key: 'a', label: 'alapjáték' },
  { key: 'r', label: 'röpte' },
];

const TRAITS: { key: SkillKey; checkLabel: string; eloLabel: string }[] = [
  { key: 'fast', checkLabel: 'gyors', eloLabel: 'gyorsaság' },
  { key: 'balanced', checkLabel: 'kiegy.', eloLabel: 'kiegyens.' },
  { key: 'cunning', checkLabel: 'ravasz', eloLabel: 'ravaszság' },
];

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

export default function CikkCalculator() {
  const [mode, setMode] = useState<CalcMode>('score');
  const [players, setPlayers] = useState<Cohort>(() => clonePreset(PRESET_SCORE));

  const pairs = useMemo(() => allPairs(mode, players), [mode, players]);
  const sortedPairs = useMemo(() => [...pairs].sort((a, b) => b.total - a.total), [pairs]);
  const partitions = useMemo(() => partitionsFromPairs(pairs), [pairs]);

  const bounds =
    mode === 'score'
      ? { min: 1, max: SCALE_MAX, step: 1 }
      : { min: ELO_MIN, max: ELO_MAX, step: ELO_STEP };

  function switchMode(next: CalcMode) {
    if (next === mode) return;
    setMode(next);
    setPlayers(clonePreset(next === 'score' ? PRESET_SCORE : PRESET_ELO));
  }

  function resetToPreset() {
    setPlayers(clonePreset(mode === 'score' ? PRESET_SCORE : PRESET_ELO));
  }

  function setField(player: PlayerLabel, field: SkillKey, value: number) {
    setPlayers((prev) => ({ ...prev, [player]: { ...prev[player], [field]: value } }));
  }

  function onNumberChange(player: PlayerLabel, field: SkillKey, raw: string) {
    const v = parseFloat(raw);
    if (Number.isNaN(v)) return;
    if (v < bounds.min || v > bounds.max) return; // ignore out-of-range until blur
    setField(player, field, v);
  }

  function onNumberBlur(player: PlayerLabel, field: SkillKey, raw: string) {
    let v = parseFloat(raw);
    if (Number.isNaN(v)) v = players[player][field];
    v = clamp(v, bounds.min, bounds.max);
    v = mode === 'elo' ? Math.round(v / ELO_STEP) * ELO_STEP : Math.round(v);
    setField(player, field, v);
  }

  return (
    <div className="interactive calculator" id="calculator">
      <p className="interactive-label">Próbáljuk ki</p>
      <h4>Páros-erősség kalkulátor négy játékossal</h4>
      <p className="intro">
        Állítsd be négy játékos képességeit és nézd meg, hogyan rakja össze a képlet a hat
        lehetséges párost, illetve hogy az optimalizáló a három lehetséges párosfelosztás
        közül melyiket választaná. Az <strong>Élő mód</strong> a finomabb és a valósághoz
        közelebb álló közelítés.
      </p>

      <div className="calc-modes" role="tablist" aria-label="Pontozási mód">
        <button
          type="button"
          className={`calc-mode-btn${mode === 'score' ? ' is-active' : ''}`}
          role="tab"
          aria-selected={mode === 'score'}
          onClick={() => switchMode('score')}
        >
          1–5 pontozás
        </button>
        <button
          type="button"
          className={`calc-mode-btn${mode === 'elo' ? ' is-active' : ''}`}
          role="tab"
          aria-selected={mode === 'elo'}
          onClick={() => switchMode('elo')}
        >
          Élő pontok
        </button>
      </div>

      {mode === 'elo' && (
        <p className="calc-disclaimer">
          Az Élő módban a számok más, logaritmikus skálán mozognak — a páros-erősségek így
          tömörebbnek látszanak. A relatív sorrend és az optimalizáló döntése azonban
          változatlan: ugyanúgy a legkisebb erőkülönbségű felállást keresi.
        </p>
      )}

      <p className="calc-actions">
        A példa egy kitalált felállást mutat különböző stílusú játékosokkal —{' '}
        <button type="button" className="calc-reset" onClick={resetToPreset}>
          alaphelyzet
        </button>
        .
      </p>

      <div className="calc-players">
        {PLAYER_LABELS.map((lbl) => (
          <div className="calc-player" key={lbl} data-player={lbl}>
            <p className="calc-player-name">
              <span className="label-letter">{lbl}</span>
              <span>játékos</span>
            </p>
            {SKILLS.map((s) => (
              <div className="calc-field" key={s.key}>
                <label htmlFor={`inp-${lbl}-${s.key}`}>{s.label}</label>
                <input
                  id={`inp-${lbl}-${s.key}`}
                  type="number"
                  inputMode="numeric"
                  min={bounds.min}
                  max={bounds.max}
                  step={bounds.step}
                  value={players[lbl][s.key]}
                  onChange={(e) => onNumberChange(lbl, s.key, e.target.value)}
                  onBlur={(e) => onNumberBlur(lbl, s.key, e.target.value)}
                />
              </div>
            ))}

            {mode === 'score' ? (
              <div className="calc-traits" data-role="traits-score">
                {TRAITS.map((t) => (
                  <label className="calc-trait-check" key={t.key}>
                    <input
                      type="checkbox"
                      checked={!!players[lbl][t.key]}
                      onChange={(e) => setField(lbl, t.key, e.target.checked ? 1 : 0)}
                    />
                    {t.checkLabel}
                  </label>
                ))}
              </div>
            ) : (
              <div className="calc-traits-elo" data-role="traits-elo">
                {TRAITS.map((t) => (
                  <div className="calc-field" key={t.key}>
                    <label htmlFor={`inp-${lbl}-${t.key}`}>{t.eloLabel}</label>
                    <input
                      id={`inp-${lbl}-${t.key}`}
                      type="number"
                      inputMode="numeric"
                      min={bounds.min}
                      max={bounds.max}
                      step={bounds.step}
                      value={players[lbl][t.key]}
                      onChange={(e) => onNumberChange(lbl, t.key, e.target.value)}
                      onBlur={(e) => onNumberBlur(lbl, t.key, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <h5
        style={{
          fontFamily: 'var(--f-display)',
          fontWeight: 600,
          fontSize: '0.78rem',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--c-green)',
          margin: '1.4rem 0 0.5rem',
        }}
      >
        A hat lehetséges páros
      </h5>
      <div style={{ overflowX: 'auto' }}>
        <table className="pair-results">
          <thead>
            <tr>
              <th className="left">Páros</th>
              <th>1. szervája</th>
              <th>2. szervája</th>
              <th>Ellenfél szervája</th>
              <th>Bónusz</th>
              <th>Összesen</th>
            </tr>
          </thead>
          <tbody>
            {sortedPairs.map((p) => (
              <tr key={`${p.a}${p.b}`}>
                <td className="left pair-name">
                  {p.a} + {p.b}
                </td>
                <td>{fmt(p.c1)}</td>
                <td>{fmt(p.c2)}</td>
                <td>{fmt(p.c3)}</td>
                <td>{fmt(p.traits)}</td>
                <td className="pair-total">{fmt(p.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="optimal-partitions">
        <h5>Lehetséges párosfelosztások</h5>
        <table className="partition-table">
          <tbody>
            {partitions.map((part, i) => (
              <tr key={`${part.p1.a}${part.p1.b}`} className={i === 0 ? 'optimal' : undefined}>
                <td className="partition-pairs">
                  {part.p1.a}+{part.p1.b} vs. {part.p2.a}+{part.p2.b}
                  {i === 0 && <span className="partition-badge">Optimális</span>}
                </td>
                <td className="partition-spread">
                  <span className="totals">
                    {fmt(part.p1.total)} <span className="sep">vs.</span> {fmt(part.p2.total)}
                  </span>
                  <span className="spread-val">eltérés {fmt(part.spread)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
